import { useCallback, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Folds long text to a few lines ("Show more"), so what's below stays in view: a task's description.
 * Put `foldRef` and `foldProps` on the box round the text; show the Show more / Show less button when
 * `foldable`. Measured with a ResizeObserver on that box, so markdown's lists, tables and code blocks
 * count at their rendered height.
 */
export function useFold() {
  const [expanded, setExpanded] = useState(false);
  const [long, setLong] = useState(false);
  const ref = useCallback((el: HTMLDivElement | null) => {
    if (!el) return undefined;
    const observer = new ResizeObserver(() => {
      // Only measured while folded: unfolded, it always fits.
      if (el.dataset.folded === 'true') setLong(el.scrollHeight > el.clientHeight + 1);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return {
    foldRef: ref,
    expanded,
    toggle: () => setExpanded((e) => !e),
    /** The text is taller than the fold, or was unfolded: show the button. */
    foldable: long || expanded,
    foldProps: {
      'data-folded': !expanded,
      className: cn(
        !expanded && 'max-h-48 overflow-hidden',
        !expanded && long && '[mask-image:linear-gradient(to_bottom,black_70%,transparent)]'
      ),
    },
  };
}
